/**
 * One re-read per burst of saves, not one per save.
 *
 * @module hooks/useRefreshAfterSaves
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/** How long saves must pause before the workspace and programme are re-read. */
export const REFRESH_AFTER_SAVES_MS = 1500;

/**
 * Autosave fires on every typing pause while a builder types, and the workspace read
 * lists every module in the tenant. A pending re-read still runs when the
 * caller unmounts, so the matrix and the readiness see the last save.
 *
 * @param afterUnmount - `at-once` for a caller whose last save answers after it
 *   unmounts, so what opens next reads it; `per-burst` for one whose windows
 *   outlive it, so their saves still share one re-read.
 * @returns What a save calls once it lands.
 */
export function useRefreshAfterSaves(
  refresh: () => Promise<unknown>,
  { afterUnmount = "at-once" }: { afterUnmount?: "at-once" | "per-burst" } = {},
): () => void {
  const refreshRef = useRef(refresh);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  useLayoutEffect(() => {
    refreshRef.current = refresh;
  });
  const run = useCallback((): void => {
    timerRef.current = null;
    // A failed read is the workspace's to report, not the save's.
    refreshRef.current().catch(() => undefined);
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        run();
      }
    };
  }, [run]);
  return useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    // A save sent as its caller unmounts finds it gone, with no burst left to
    // wait out.
    if (!mountedRef.current && afterUnmount === "at-once") {
      run();
      return;
    }
    timerRef.current = setTimeout(run, REFRESH_AFTER_SAVES_MS);
  }, [afterUnmount, run]);
}
